import type { ControlMode, InputFrame, Pose } from '../controls/types';
import type { CollisionWorld } from '../nav/types';
import { INITIAL_VISITOR_CAMERA, updateVisitorCamera } from './visitorCamera';

export function behindPlayerPose(
  pose: Pose,
  yaw: number,
  elevation: number,
  collision: CollisionWorld,
  distance = 4
): Pose {
  const bearing = pose.yaw + yaw;
  const horizontal = distance * Math.cos(elevation);
  const from: readonly [number, number] = [pose.position[0], -pose.position[2]];
  const delta: readonly [number, number] = [
    Math.sin(bearing) * horizontal,
    -Math.cos(bearing) * horizontal,
  ];
  const desired: readonly [number, number] = [from[0] + delta[0], from[1] + delta[1]];
  const ground = collision.contains(from) ? collision.move(from, delta) : desired;
  const actual = Math.hypot(ground[0] - from[0], ground[1] - from[1]);
  return {
    ...pose,
    position: [ground[0], pose.position[1] - 0.35 + actual * Math.tan(elevation), -ground[1]],
    yaw: actual > 0.001 ? Math.atan2(ground[0] - from[0], -(ground[1] - from[1])) : bearing,
    pitch: -elevation,
    roll: 0,
    fovYDeg: 60,
  };
}

export function createVisitorView(doc: Document) {
  let behind = false;
  let camera = INITIAL_VISITOR_CAMERA;

  function toggle(): void {
    behind = !behind;
    camera = INITIAL_VISITOR_CAMERA;
    if (doc.pointerLockElement) doc.exitPointerLock();
    doc.body.dataset.cameraView = behind ? 'behind' : 'first';
  }

  const key = (event: KeyboardEvent): void => {
    if (
      doc.body.classList.contains('entry-open') ||
      event.code !== 'KeyV' ||
      event.repeat ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey
    )
      return;
    if (
      event.target instanceof HTMLElement &&
      event.target.closest('input, select, textarea, [role="dialog"]')
    )
      return;
    event.preventDefault();
    toggle();
  };
  doc.addEventListener('keydown', key);
  return {
    toggle,
    get behind(): boolean {
      return behind;
    },
    prepareInput(frame: InputFrame, mode: ControlMode, dt: number): InputFrame {
      if (!behind) return frame;
      const next = updateVisitorCamera(camera, frame, mode, dt);
      camera = next.state;
      return next.frame;
    },
    cameraPose: (pose: Pose, collision: CollisionWorld): Pose =>
      behindPlayerPose(pose, camera.yaw, camera.elevation, collision, camera.distance),
    dispose(): void {
      doc.removeEventListener('keydown', key);
    },
  };
}
